import { Assets, Texture } from 'pixi.js';
import { SVG_ASSETS, AUDIO_ASSETS, type SvgAsset } from '../config/assets';
import type { AudioBus } from './audio';

/**
 * Resolve a path under `/public` for fetch / Howler. Must respect Vite
 * `base` (e.g. `./` for static hosts like itch.io). Leading `/` would load
 * from the site root and breaks when the game is not at `/`.
 */
function publicAssetUrl(path: string): string {
  const normalized = path.replace(/^\/+/, '');
  const base = import.meta.env.BASE_URL;
  if (base === './' || base === '.') {
    return `./${normalized}`;
  }
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return `${prefix}${normalized}`;
}

/**
 * Batch asset loader.
 *
 * SVG handling: Pixi v8's built-in SVG loader relies on the Image element's
 * intrinsic size, which varies by browser when the SVG has a viewBox but no
 * explicit width/height attributes. To guarantee pixel-exact parity with the
 * sizes declared in the manifest (which the entire codebase uses in sprite
 * scaling math), we rasterize each SVG to an offscreen canvas at the EXACT
 * declared dimensions, then register that canvas as a Pixi Texture under the
 * asset key. Downstream `Texture.from(key)` calls receive a texture whose
 * `.width` / `.height` equal the manifest values, period.
 *
 * Resolution: an extra 2× bump sharpens edges on HiDPI without changing
 * the reported logical dimensions (Pixi treats resolution as a separate axis).
 */
export async function loadAllAssets(
  audio: AudioBus,
  onProgress: (pct: number) => void
): Promise<void> {
  const total = SVG_ASSETS.length + AUDIO_ASSETS.length;
  let done = 0;
  const tick = (): void => {
    done += 1;
    onProgress(done / total);
  };

  for (const asset of SVG_ASSETS) {
    try {
      const texture = await rasterizeSvgToTexture(asset);
      // Register the rasterized texture under the asset alias so every
      // `Texture.from(asset.key)` across the codebase picks it up.
      Assets.cache.set(asset.key, texture);
    } catch (err) {
      console.warn(`[assets] SVG failed to rasterize: ${asset.key}`, err);
    }
    tick();
  }

  for (const def of AUDIO_ASSETS) {
    await audio.register({
      key: def.key,
      src: [publicAssetUrl(def.path)],
      loop: def.loop,
    });
    tick();
  }
}

async function rasterizeSvgToTexture(asset: SvgAsset): Promise<Texture> {
  const url = publicAssetUrl(asset.path);
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
  const svgText = await response.text();

  // Natural dims come from the manifest; fall back to a sane default so
  // undeclared SVGs still render. We also inject explicit width/height attrs
  // on the <svg> root to guarantee consistent Image.decode() sizing.
  const targetW = asset.width ?? 256;
  const targetH = asset.height ?? 256;
  const sizedSvg = injectExplicitSize(svgText, targetW, targetH);

  const blob = new Blob([sizedSvg], { type: 'image/svg+xml' });
  const blobUrl = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = 'sync';
    img.src = blobUrl;
    await img.decode();

    const resolution = Math.min(window.devicePixelRatio || 1, 2);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(targetW * resolution);
    canvas.height = Math.round(targetH * resolution);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Wrap the canvas in a Pixi Texture. Pass `resolution` so the source's
    // logical size (targetW × targetH) is reported to the renderer while the
    // backing canvas stays at `targetW × resolution` for crisp HiDPI output.
    // NOTE: Setting `source.resolution` after `Texture.from(canvas)` does NOT
    // rescale the already-constructed source — width/height were baked in at
    // creation time, so the sprite would render 2× too big on HiDPI.
    const texture = Texture.from({
      resource: canvas,
      resolution,
    });
    return texture;
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

/**
 * Replace (or add) the width/height attributes on the root <svg> element so
 * the browser's Image decoder uses the manifest-declared dimensions. Preserves
 * the original viewBox for correct internal coordinate mapping.
 */
function injectExplicitSize(svgText: string, width: number, height: number): string {
  const svgTagMatch = svgText.match(/<svg\b[^>]*>/i);
  if (!svgTagMatch) return svgText;
  const original = svgTagMatch[0];
  let patched = original
    .replace(/\swidth="[^"]*"/i, '')
    .replace(/\sheight="[^"]*"/i, '');
  // Insert new width/height just after the opening `<svg`.
  patched = patched.replace(/<svg\b/, `<svg width="${width}" height="${height}"`);
  return svgText.replace(original, patched);
}

export const tex = (key: string): Texture => Texture.from(key);
